import type { QueryKey } from '@tanstack/react-query';

import type { FilterState } from '../types/filters';
import type { ScoreFilters } from '../types/scores';
import { ViewTypes } from '../utils/filterConstants';

export const cardKeys = {
  all: ['cards'] as const,
  list: (viewType: string, filters: FilterState, extra?: Record<string, unknown>) =>
    [...cardKeys.all, viewType, filters, extra] as const,
};

export const playerKeys = {
  all: ['player'] as const,
  detail: (slug: string) => [...playerKeys.all, slug] as const,
  nbaDetail: (slug: string) => [...playerKeys.all, 'nba', slug] as const,
  tab: (slug: string, tab: string, rarity: string) =>
    [...playerKeys.all, slug, tab, rarity] as const,
  scores: (slug: string, filters: ScoreFilters) =>
    [...playerKeys.all, slug, 'scores', filters] as const,
  nextGames: (slug: string, limit: number) =>
    [...playerKeys.all, slug, 'nextGames', limit] as const,
  search: (text: string) => [...playerKeys.all, 'search', text] as const,
};

export const topPlayersKeys = {
  all: ['topPlayers'] as const,
  list: (filters: FilterState) => [...topPlayersKeys.all, filters] as const,
};

export const topNbaPlayersKeys = {
  all: ['topNbaPlayers'] as const,
  list: (filters: FilterState) => [...topNbaPlayersKeys.all, filters] as const,
};

export const nbaLineupKeys = {
  all: ['nbaLineup'] as const,
  list: (slug: string, filters: FilterState) => [...nbaLineupKeys.all, slug, filters] as const,
};

export const dashboardKeys = {
  all: ['dashboard'] as const,
  stats: () => [...dashboardKeys.all, 'stats'] as const,
};

export const authKeys = {
  all: ['auth'] as const,
  session: () => [...authKeys.all, 'session'] as const,
};

/**
 * The query-key scope whose fetches belong to a given filter-bar `view` — every view
 * that isn't one of the three dedicated lists reads its cards through `cardKeys`.
 */
export const getViewQueryScope = (view: string): QueryKey => {
  switch (view) {
    case ViewTypes.TOP_PLAYERS:
      return topPlayersKeys.all;
    case ViewTypes.TOP_NBA_PLAYERS:
      return topNbaPlayersKeys.all;
    case ViewTypes.MANAGER_NBA_LINEUP:
      return nbaLineupKeys.all;
    default:
      return cardKeys.all;
  }
};

export const selectOptionsKeys = {
  all: ['selectOptions'] as const,
  clubs: (type: string) => [...selectOptionsKeys.all, 'clubs', type] as const,
  leagues: (type: string) => [...selectOptionsKeys.all, 'leagues', type] as const,
  countries: () => [...selectOptionsKeys.all, 'countries'] as const,
  editions: () => [...selectOptionsKeys.all, 'editions'] as const,
  gameWeeks: () => [...selectOptionsKeys.all, 'gameWeeks'] as const,
};
